import AnimationController from "./animation"
import confetti from 'canvas-confetti'

export default class extends AnimationController {
    static targets = ["progressCircle", "progressText", "checklistItem", "confettiCanvas", "headingText", "animatedElement"]
    static values = {
        delay: { type: Number, default: 700 }, // Override default delay
        finalDelay: { type: Number, default: 300 }, // Override default final delay
        started: { type: Boolean, default: false },
        completed: { type: Boolean, default: false },
        progress: { type: Number, default: 0 }
    }

    connect() {
        super.connect() // Call parent's connect method

        if (this.hasConfettiCanvasTarget) {
            this.confetti = confetti.create(this.confettiCanvasTarget, {
                resize: true,
                useWorker: false,
                disableForReducedMotion: true
            })
        }
    }

    disconnect() {
        clearTimeout(this.confettiBurstTimeout)
        this.confetti?.reset()
        this.confetti = null

        super.disconnect() // Call parent's disconnect method
    }

    handleStepShown(event) {
        if (event.detail.stepKey !== "skin_profile_generation") return

        if (this.completedValue) {
            this.notifyAnimationsComplete()
        } else if (!this.startedValue) {
            this.startedValue = true
            this.animate()
        }
    }

    animate() {
        // Reset state
        this.setProgressCircle(0)
        this.resetAnimations()

        // Start progress animation
        this.animateProgress()
    }

    animateProgress() {
        const duration = 2000 // 2 seconds for the progress circle animation
        const startTime = Date.now()
        const itemThresholds = [25, 50, 75] // Show items at these progress percentages

        const animate = () => {
            const currentTime = Date.now()
            const elapsed = currentTime - startTime

            if (elapsed < duration) {
                const progress = Math.round((elapsed / duration) * 100)
                this.progressValue = progress
                this.setProgressCircle(progress)

                // Reveal checklist items based on progress
                this.updateChecklistVisibility(progress, itemThresholds)

                requestAnimationFrame(animate)
            } else {
                this.progressValue = 100
                this.setProgressCircle(100)

                // Show all items
                this.showAllChecklist()

                // Change heading text
                if (this.hasHeadingTextTarget) {
                    this.headingTextTarget.classList.add("opacity-0")
                    setTimeout(() => {
                        this.headingTextTarget.textContent = "Your profile is ready!"
                        this.headingTextTarget.classList.remove("opacity-0")
                    }, 300)
                }

                // Complete animation with confetti
                setTimeout(() => {
                    this.triggerConfetti()
                    this.completedValue = true
                    this.notifyAnimationsComplete()
                }, 300)
            }
        }

        animate()
    }

    updateChecklistVisibility(progress, thresholds) {
        if (!this.hasChecklistItemTarget) return

        const items = this.checklistItemTargets

        // Show each item when its threshold is reached
        items.forEach((item, index) => {
            if (progress >= thresholds[index] && item.classList.contains('opacity-0')) {
                item.classList.remove("opacity-0")
                item.classList.add("opacity-100")
            }
        })
    }

    showAllChecklist() {
        if (!this.hasChecklistItemTarget) return

        this.checklistItemTargets.forEach(item => {
            item.classList.remove("opacity-0")
            item.classList.add("opacity-100")
        })
    }

    setProgressCircle(percent) {
        if (this.hasProgressTextTarget) {
            this.progressTextTarget.textContent = `${percent}%`
        }

        if (this.hasProgressCircleTarget) {
            // Calculate the stroke-dashoffset based on percentage
            // For a circle with radius 40, circumference is 2 * PI * 40 = 251.327
            const circumference = 251.327
            const dashoffset = circumference - (percent / 100) * circumference

            this.progressCircleTarget.style.strokeDashoffset = dashoffset
        }
    }

    triggerConfetti() {
        if (!this.confetti) {
            console.error('Canvas confetti not available')
            return
        }

        const colors = ['#6C47FF', '#34D399', '#FBBF24']
        const commonConfig = {
            colors,
            gravity: 0.8,
            origin: { y: 0.5 }
        }

        this.confetti({
            ...commonConfig,
            particleCount: 80,
            spread: 60,
            scalar: 1.2
        })

        this.confettiBurstTimeout = setTimeout(() => {
            this.confetti?.({
                ...commonConfig,
                particleCount: 35,
                spread: 45,
                scalar: 1.1,
                angle: 60,
                origin: { ...commonConfig.origin, x: 0 }
            })
            this.confetti?.({
                ...commonConfig,
                particleCount: 35,
                spread: 45,
                scalar: 1.1,
                angle: 120,
                origin: { ...commonConfig.origin, x: 1 }
            })
        }, 250)
    }
}
